import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

const ManualControlModal = ({ isOpen, onClose, onSave, initialData }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [measureType, setMeasureType] = useState('protection');

    // Sync state with initialData when modal opens or initialData changes
    React.useEffect(() => {
        if (isOpen && initialData) {
            setName(initialData.name || '');
            setDescription(initialData.description || '');
            setMeasureType(initialData.measure_type || 'protection');
        } else if (isOpen && !initialData) {
            // Reset state for "Add" mode
            setName('');
            setDescription('');
            setMeasureType('protection');
        }
    }, [isOpen, initialData]);

    if (!isOpen) return null;

    const handleSave = () => {
        if (name.trim()) {
            onSave({
                name,
                description,
                measureType,
                id: initialData?.id // Pass along the ID if editing
            });
            onClose();
        }
    };

    const measureTypeOptions = [
        { value: 'protection', label: t('securityControls.modal.measureTypes.protection', 'Protection') },
        { value: 'défense', label: t('securityControls.modal.measureTypes.defense', 'Défense') },
        { value: 'résilience', label: t('securityControls.modal.measureTypes.resilience', 'Résilience') },
        { value: 'gouvernance et anticipation', label: t('securityControls.modal.measureTypes.governance', 'Gouvernance et anticipation') }
    ];

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] p-4 print:hidden">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md relative">
                <button onClick={onClose} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"><X size={20} /></button>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {initialData ? t('securityControls.modal.editManualControl', 'Modifier le contrôle') : t('securityControls.modal.addManualControl', 'Ajouter un contrôle manuel')}
                </h3>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('securityControls.fields.name', 'Nom du contrôle')}</label>
                        <input
                            type="text"
                            className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('securityControls.placeholders.controlName', 'Ex: Politique de mots de passe...')}
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('securityControls.modal.measureType', 'Type de mesure')}</label>
                        <select
                            className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            value={measureType}
                            onChange={(e) => setMeasureType(e.target.value)}
                        >
                            {measureTypeOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('securityControls.fields.description', 'Description')}</label>
                        <textarea
                            className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                            rows="3"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t('securityControls.placeholders.controlDescription', 'Description du contrôle...')}
                        />
                    </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm">{t('common.cancel', 'Annuler')}</button>
                    <button
                        onClick={handleSave}
                        disabled={!name.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50"
                    >
                        {initialData ? t('common.save', 'Enregistrer') : t('common.add', 'Ajouter')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ManualControlModal;
